import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { groupPath } from '../lib/groupPath';
import { JOIN_CODE_LENGTH, normalizeJoinCode } from '../lib/joinCode';
import type { Group } from '../lib/types';
import { useStore } from '../store/useStore';

type Tab = 'create' | 'join';

/** Shown to an account that belongs to no team yet: start one, or join one. */
export function Onboarding() {
  const logout = useStore((s) => s.logout);
  const user = useStore((s) => s.user);

  return (
    <Frame
      title={`Welcome, ${user?.displayName.split(' ')[0] ?? 'there'}`}
      intro="You aren't part of a team yet. Create one and you own it, or join a team someone else already set up."
      footer={
        <button
          type="button"
          onClick={() => void logout()}
          className="mt-4 w-full text-xs text-ink-faint transition-colors hover:text-ink-soft"
        >
          Sign out
        </button>
      }
    />
  );
}

/**
 * The same two forms, reached from inside the app. Without this there is no way
 * to reach a second team: an account that already has one never sees onboarding
 * again, so a join code or an invite for another team had nowhere to go.
 */
export function AddTeam() {
  const groups = useStore((s) => s.groups);
  const last = groups[0];

  return (
    <Frame
      title="Another team"
      intro="An account can belong to as many teams as it likes. Each one keeps its own board, dev fields, members and releases — nothing is shared between them."
      footer={
        last ? (
          <Link
            to={groupPath(last)}
            className="mt-4 block w-full text-center text-xs text-ink-faint transition-colors hover:text-ink-soft"
          >
            Back to the board
          </Link>
        ) : null
      }
    />
  );
}

function Frame({
  title,
  intro,
  footer,
}: {
  title: string;
  intro: string;
  footer: React.ReactNode;
}) {
  const [tab, setTab] = useState<Tab>('create');
  const navigate = useNavigate();

  // The new team is opened by its own URL, so it is bookmarkable from the first
  // moment and the browser's back button behaves.
  const openTeam = (group: Group): void => {
    void navigate(groupPath(group), { replace: true });
  };

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-10">
      <div className="animate-rise w-full max-w-md">
        <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{intro}</p>

        <div className="mt-6 flex gap-1 rounded-lg border border-line bg-surface p-1">
          {(
            [
              ['create', 'Create a team'],
              ['join', 'Join a team'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm transition-colors ${
                tab === value
                  ? 'bg-elevated font-medium text-ink'
                  : 'text-ink-soft hover:bg-hover hover:text-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'create' ? <CreateTeam onDone={openTeam} /> : <JoinTeam onDone={openTeam} />}

        {footer}
      </div>
    </div>
  );
}

function CreateTeam({ onDone }: { onDone: (group: Group) => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createGroup = useStore((s) => s.createGroup);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(await createGroup(name));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create the team');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card animate-rise mt-4 flex flex-col gap-4 p-5">
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-ink-soft">Team name</span>
        <input
          className="field-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={80}
          placeholder="Boom Network"
        />
      </label>

      <p className="text-xs leading-relaxed text-ink-faint">
        Three dev fields — Plugins, Web and Infrastructure — are created for you. You can rename them
        or add more in Settings, where you will also find the join code to hand to your team.
      </p>

      <ErrorLine message={error} />

      <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()}>
        {busy ? 'Creating…' : 'Create team'}
      </button>
    </form>
  );
}

function JoinTeam({ onDone }: { onDone: (group: Group) => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const joinGroup = useStore((s) => s.joinGroup);

  const normalized = normalizeJoinCode(code);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(await joinGroup(normalized));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not join that team');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card animate-rise mt-4 flex flex-col gap-4 p-5">
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-ink-soft">Join code</span>
        <input
          className="field-input font-mono tracking-widest uppercase"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          maxLength={20}
          placeholder="ABCD-2345"
          autoComplete="off"
          spellCheck={false}
        />
      </label>

      <p className="text-xs leading-relaxed text-ink-faint">
        An owner or admin of the team finds this in Settings. An invite link works too — open it and
        you are added straight away.
      </p>

      <ErrorLine message={error} />

      <button
        type="submit"
        className="btn btn-primary"
        disabled={busy || normalized.length !== JOIN_CODE_LENGTH}
      >
        {busy ? 'Joining…' : 'Join team'}
      </button>
    </form>
  );
}

function ErrorLine({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
      {message}
    </p>
  );
}
